import { useEffect, useMemo, useState } from "react";
import { advisoryApi, type RiskCurrent, type RiskOptions } from "../advisoryApi";
import { reasonText } from "../format";
import { PrefillNote, SaveBar, Seg } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface RiskData {
  options: RiskOptions;
  current: RiskCurrent | null;
}

const FAILURE_TEXT: Record<string, string> = {
  no_risk_score: "No score yet — choose a time horizon and a risk level.",
  no_portfolio_for_score: "No model portfolio is configured for this score. A custom portfolio can still be built.",
  no_portfolio_for_platform: "No model portfolio exists for this score on the chosen platform.",
};

/**
 * Risk assessment (`risk_assessment`) — QP's risk-assessment-widget: time
 * horizon × risk level (the matrix) plus the adjustment questions, the score
 * breakdown and any cap. An incomplete answer is kept as a draft (PATCH), a
 * complete one is scored (PUT), as QP does.
 */
export function RiskStep({ sessionId, addr, steps, readOnly, onSaved, prefill, clearPrefill }: StepProps) {
  const goalAddr = { goalId: addr.goalId };
  const load = useStepLoad<RiskData>(sessionId, "risk-assessment", goalAddr);
  const saver = useSaver(onSaved);
  const [horizon, setHorizon] = useState<string | null>(null);
  const [level, setLevel] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [base, setBase] = useState("");
  const [prefillUsed, setPrefillUsed] = useState(false);

  const snapshot = (h: string | null, l: string | null, a: Record<string, string>) => JSON.stringify([h, l, a]);

  useEffect(() => {
    const c = load.data?.current;
    if (!load.data) return;
    const h = c?.timeHorizonId ?? null;
    const l = c?.riskLevelId ?? null;
    const a = c?.adjustmentAnswers ?? {};
    setHorizon(h);
    setLevel(l);
    setAnswers(a);
    setBase(snapshot(h, l, a));
  }, [load.data]);

  // Andersen's risk profile → proposed risk level (shown, not saved).
  useEffect(() => {
    const id = prefill?.proposal.riskLevelId;
    if (typeof id === "string") {
      setLevel(id);
      setPrefillUsed(true);
    }
  }, [prefill]);

  const opts = load.data?.options;
  const horizons = useMemo(() => [...(opts?.timeHorizons ?? [])].sort((a, b) => a.order - b.order), [opts]);
  const levels = useMemo(() => [...(opts?.riskLevels ?? [])].sort((a, b) => a.order - b.order), [opts]);

  if (!addr.goalId) return <p className="muted">This page needs a goal.</p>;
  if (load.loading && !load.data) return <p className="muted">Loading the risk model…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;

  const questions = opts?.adjustmentQuestions ?? [];
  const complete = !!horizon && !!level && questions.every((q) => answers[q.id]);
  const dirty = snapshot(horizon, level, answers) !== base;
  const result = load.data?.current?.result ?? null;
  const cap = opts?.cap;
  const openReason = steps.find((s) => s.status === "open")?.reason;

  async function save() {
    const body: Record<string, unknown> = {
      ...(horizon ? { timeHorizonId: horizon } : {}),
      ...(level ? { riskLevelId: level } : {}),
      adjustmentAnswers: answers,
      ...(complete ? {} : { partial: true }),
      ...(prefillUsed ? { prefillUsed: true } : {}),
    };
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "risk-assessment", goalAddr, body), {
      goalsChanged: true,
    });
    if (r) {
      setBase(snapshot(horizon, level, answers));
      setPrefillUsed(false);
      clearPrefill();
      load.reload();
    }
  }

  return (
    <section className="adv-step-card">
      {prefill && <PrefillNote basis={prefill.basis} onDismiss={clearPrefill} />}

      <div className="field">
        <span>Time horizon *</span>
        <Seg
          value={horizon}
          options={horizons.map((h) => ({ value: h.id, label: h.label }))}
          onChange={setHorizon}
          disabled={readOnly}
          label="Time horizon"
        />
      </div>
      <div className="field">
        <span>Risk level *</span>
        <Seg
          value={level}
          options={levels.map((l) => ({ value: l.id, label: l.label }))}
          onChange={setLevel}
          disabled={readOnly}
          label="Risk level"
        />
      </div>

      {questions.map((q) => (
        <fieldset key={q.id} className="adv-question">
          <legend>{q.label.trim()}</legend>
          <div className="option-list">
            {q.answers.map((a) => (
              <label key={a.id} className={answers[q.id] === a.id ? "option-row selected" : "option-row"}>
                <input
                  type="radio"
                  name={`adj-${q.id}`}
                  checked={answers[q.id] === a.id}
                  onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: a.id }))}
                  disabled={readOnly}
                />
                {a.label.trim()}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      {result && result.score != null ? (
        <div className="adv-subsection">
          <span className="adv-label">Risk score</span>
          <div className="perf-strip">
            <div className="perf-figure">
              <span className="label">Score</span>
              <span className="value">{result.score}</span>
            </div>
            <div className="perf-figure">
              <span className="label">From matrix</span>
              <span className="value">{result.scoreFromMatrix ?? "—"}</span>
            </div>
            <div className="perf-figure">
              <span className="label">Adjustment</span>
              <span className="value">
                {result.scoreAdjustment ? (result.scoreAdjustment > 0 ? `+${result.scoreAdjustment}` : result.scoreAdjustment) : "0"}
              </span>
            </div>
            <div className="perf-figure">
              <span className="label">Cap</span>
              <span className="value">{result.isCapped ? `Capped${cap?.level ? ` at ${cap.level}` : ""}` : "None"}</span>
            </div>
          </div>
          {result.modelResolutionFailure && (
            <p className="adv-calm-note">{FAILURE_TEXT[result.modelResolutionFailure] ?? result.modelResolutionFailure}</p>
          )}
        </div>
      ) : (
        openReason && <p className="muted">{reasonText(openReason)}</p>
      )}
      {cap?.applies && !result?.isCapped && (
        <p className="muted">A cap applies to this client{cap.level ? ` (level ${cap.level})` : ""}.</p>
      )}

      <SaveBar
        onSave={save}
        saving={saver.saving}
        dirty={dirty}
        error={saver.error}
        readOnly={readOnly}
        label={complete ? "Save and score" : "Save draft"}
      />
    </section>
  );
}
